import { Random } from '../utils/Random.js';
import { NPCPopulation } from './NPCPopulation.js';

/**
 * Spawns NPCs and picks destinations with density + personality bias.
 */
export class NPCSpawner {
  /**
   * @param {import('./NavigationGraph.js').NavigationGraph} graph
   * @param {number} seed
   * @param {import('../config.js').CONFIG['city']} cityConfig
   * @param {import('../config.js').CONFIG['npc']} npcConfig
   */
  constructor(graph, seed, cityConfig, npcConfig) {
    this.graph = graph;
    this.rng = new Random(seed);
    this.config = npcConfig;
    this.population = new NPCPopulation(cityConfig, npcConfig.population);
  }

  /** @param {import('./NPCPersonality.js').NPCPersonality} personality */
  pickSpawnParams(personality) {
    const nodeId = this._pickWeightedNode(personality, 18);
    const speed =
      this.rng.range(this.config.speedMin, this.config.speedMax) *
      personality.speedMultiplier;
    return { nodeId, speed };
  }

  /**
   * @param {number} fromNodeId
   * @param {import('./NPCPersonality.js').NPCPersonality} personality
   * @returns {number}
   */
  pickDestination(fromNodeId, personality) {
    const from = this.graph.getNode(fromNodeId);
    if (!from) return fromNodeId;

    let bestId = fromNodeId;
    let bestScore = -Infinity;
    const samples = this.config.destinationSamples ?? 12;

    for (let i = 0; i < samples; i += 1) {
      const candidate = this.graph.getRandomNodeId(this.rng);
      if (candidate === fromNodeId) continue;
      const node = this.graph.getNode(candidate);
      if (!node) continue;

      const dist = Math.hypot(node.x - from.x, node.z - from.z);
      const weight = this.population.weightForNode(node);
      let score = weight * (2 + this.rng.next() * 3);

      if (personality.destinationBias > 0.5) {
        score += (dist / 40) * personality.destinationBias;
      } else {
        score += (1 - dist / 120) * (1 - personality.destinationBias);
      }

      if (personality.detourAffinity > 0.55 && node.kind === 'intersection') {
        score += personality.detourAffinity * 1.2;
      }

      score += personality.timerJitter * 0.01 * (this.rng.next() - 0.5);

      if (score > bestScore) {
        bestScore = score;
        bestId = candidate;
      }
    }

    if (bestId === fromNodeId) {
      return (fromNodeId + 1 + this.rng.int(0, 8)) % this.graph.nodes.length;
    }
    return bestId;
  }

  /**
   * @param {import('./NPCPersonality.js').NPCPersonality} personality
   * @param {number} maxAttempts
   */
  _pickWeightedNode(personality, maxAttempts) {
    if (this.graph.nodes.length === 0) return 0;

    let bestId = this.graph.getRandomNodeId(this.rng);
    let bestScore = -Infinity;

    for (let a = 0; a < maxAttempts; a += 1) {
      const id = this.graph.getRandomNodeId(this.rng);
      const node = this.graph.getNode(id);
      if (!node) continue;
      const w = this.population.weightForNode(node);
      const score = w + this.rng.next() * 0.35 + personality.timerJitter * 0.02;
      if (score > bestScore) {
        bestScore = score;
        bestId = id;
      }
    }
    return bestId;
  }
}
